import { YearInReviewData } from "@ugg/shared/api/requests/summoner-profiles/year-in-review";

export interface HighchartsLPDataPoint {
  x: number;
  y: number;
  data: {
    tier: string;
    rank: string;
    lp: number;
    championId?: number;
    timestamp: number;
  };
  diff: number;
}

const START_MONTH_INDEX = 4; // May (0-indexed)
const END_MONTH_INDEX = 11; // Dec (0-indexed)

/**
 * Generate labels specifically for May through December
 */
export function generateMonthLabels(): { categories: string[]; tickPositions: number[] } {
  const categories = ["MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

  const currentYear = new Date().getFullYear();
  const startTimestamp = new Date(currentYear, START_MONTH_INDEX, 1).getTime();
  const endTimestamp = new Date(currentYear, END_MONTH_INDEX, 31, 23, 59, 59).getTime();
  const totalDuration = endTimestamp - startTimestamp;

  const tickPositions = categories.map((_, index) => {
    // Get timestamp for 1st of this month
    const monthStart = new Date(currentYear, START_MONTH_INDEX + index, 1).getTime();
    return (monthStart - startTimestamp) / totalDuration;
  });

  return { categories, tickPositions };
}

/**
 * Get the fractional position within the May-Dec period (0-1)
 */
function getPeriodFraction(timestamp: number): number {
  const date = new Date(timestamp);
  const year = date.getFullYear();

  const startOfPeriod = new Date(year, START_MONTH_INDEX, 1).getTime();
  const endOfPeriod = new Date(year, END_MONTH_INDEX, 31, 23, 59, 59, 999).getTime();

  const duration = endOfPeriod - startOfPeriod;

  const fraction = (timestamp - startOfPeriod) / duration;
  return Math.max(0, Math.min(1, fraction));
}

function isDateOnMonthDay(timestamp: number, month: number, day: number): boolean {
  const date = new Date(timestamp);
  return date.getMonth() === month && date.getDate() === day;
}

interface ProcessedEntry {
  champion_id?: number;
  diff?: number;
  lp: number;
  match_creation_time?: string;
  match_id?: number;
  rank: string;
  score: number;
  tier: string;
  timestamp: number;
}

export function transformLPTimeline(lpTimeline: YearInReviewData["lp_timeline"]): HighchartsLPDataPoint[] {
  if (!lpTimeline || !Array.isArray(lpTimeline) || lpTimeline.length === 0) {
    return [];
  }

  const currentYear = new Date().getFullYear(); // Or parse from data if needed
  const startOfPeriod = new Date(currentYear, START_MONTH_INDEX, 1).getTime();

  // 1. Parse and Filter: Only keep entries on or after May 1st
  const validEntries: ProcessedEntry[] = [];

  for (const entry of lpTimeline) {
    if (entry.score === undefined) continue;
    const timestamp = entry.match_creation_time ? new Date(entry.match_creation_time).getTime() : 0;

    // Filter out data before May 1st
    if (timestamp < startOfPeriod) continue;

    validEntries.push({ ...entry, timestamp });
  }

  // 2. Sort
  validEntries.sort((a, b) => a.timestamp - b.timestamp);

  // Handle case where user has no games after May 1st
  if (validEntries.length === 0) {
    // Optional: You might want to pull the LAST game from before May 1st
    // to use as the starting point, otherwise the graph is empty.
    // For now, returning empty array.
    return [];
  }

  const firstEntry = validEntries[0];
  const lastEntry = validEntries[validEntries.length - 1];
  const year = new Date(firstEntry.timestamp).getFullYear();

  const extendedEntries: ProcessedEntry[] = [...validEntries];

  // 3. Add synthetic May 1st point
  // If the first game played wasn't exactly on May 1st, we project the first known rank
  // back to May 1st so the line starts at the left edge of the chart.
  if (!isDateOnMonthDay(firstEntry.timestamp, START_MONTH_INDEX, 1)) {
    extendedEntries.unshift({
      timestamp: new Date(year, START_MONTH_INDEX, 1).getTime(),
      tier: firstEntry.tier,
      rank: firstEntry.rank,
      lp: firstEntry.lp,
      score: firstEntry.score,
      champion_id: firstEntry.champion_id,
    });
  }

  // 4. Add synthetic Dec 31st point
  if (!isDateOnMonthDay(lastEntry.timestamp, END_MONTH_INDEX, 31)) {
    extendedEntries.push({
      timestamp: new Date(year, END_MONTH_INDEX, 31).getTime(),
      tier: lastEntry.tier,
      rank: lastEntry.rank,
      lp: lastEntry.lp,
      score: lastEntry.score,
      champion_id: lastEntry.champion_id,
    });
  }

  // 5. Map to Highcharts format
  return extendedEntries.map((entry, index) => {
    const prevScore = index > 0 ? extendedEntries[index - 1].score : entry.score;

    return {
      x: getPeriodFraction(entry.timestamp),
      y: entry.score,
      data: {
        tier: entry.tier,
        rank: entry.rank,
        lp: entry.lp,
        championId: entry.champion_id,
        timestamp: entry.timestamp,
      },
      diff: entry.score - prevScore,
    };
  });
}

export function calculateYAxisBounds(series: HighchartsLPDataPoint[]): { min: number; max: number } {
  if (!series || series.length === 0) return { min: 0, max: 4000 };
  const yValues = series.map((point) => point.y);
  const min = Math.min(...yValues);
  const max = Math.max(...yValues);
  return { min: Math.max(0, min - 100), max: max + 100 };
}
